import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Car, ArrowRight, Search, ShieldCheck, TrendingDown, Truck, Star, Clock, BookOpen, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Button } from "../components/ui/button";
import { Footer } from "../components/Footer";
import axios from "axios";

const envBackend = process.env.REACT_APP_BACKEND_URL;
const API = envBackend ? `${envBackend}/api` : (window.APP_CONFIG?.API_URL || '/api');

export default function HomePage() {
  const [vehicles, setVehicles] = useState([]);
  const [reviews, setReviews] = useState([]);
  const stockRef = useRef(null);
  const reviewRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/vehicles`).then(r => setVehicles((r.data.vehicles || []).slice(0, 8))).catch(() => {});
    axios.get(`${API}/reviews`).then(r => setReviews(r.data.reviews || [])).catch(() => {});
  }, []);

  const scroll = (ref, dir) => {
    if (ref.current) ref.current.scrollBy({ left: dir * 320, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-[rgba(138,138,141,0.1)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/">
            <img src="/logo-telobajo-new.png" alt="Te Lo Bajo" className="h-14 sm:h-16 w-auto" data-testid="logo-image" />
          </Link>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <Link to="/importacion" className="flex items-center gap-1.5 bg-[#FF3B30] hover:bg-[#E0352B] text-white text-[11px] sm:text-sm font-bold px-2.5 sm:px-4 py-2 rounded-full transition-colors" data-testid="nav-importacion">
              <span className="hidden sm:inline">Importación</span><span className="sm:hidden">Import</span>
            </Link>
            <Link to="/ocasion" className="flex items-center gap-1.5 bg-[#007AFF] hover:bg-[#006AE0] text-white text-[11px] sm:text-sm font-bold px-2.5 sm:px-4 py-2 rounded-full transition-colors" data-testid="nav-nacionales">
              <span className="hidden sm:inline">Nacionales</span><span className="sm:hidden">Nacional</span>
            </Link>
            <Link to="/coches-disponibles" className="flex items-center gap-1.5 bg-[#34C759] hover:bg-[#2DB84E] text-white text-[11px] sm:text-sm font-bold px-2.5 sm:px-4 py-2 rounded-full transition-colors" data-testid="nav-stock">
              <Car className="w-4 h-4" /><span className="hidden sm:inline">Stock</span><span className="sm:hidden">Stock</span>
            </Link>
            <Link to="/blog" className="flex items-center gap-1.5 bg-[#F5A623] hover:bg-[#D4901E] text-white text-[11px] sm:text-sm font-bold px-2.5 sm:px-4 py-2 rounded-full transition-colors" data-testid="nav-blog">
              <BookOpen className="w-4 h-4" /><span className="hidden sm:inline">Blog</span><span className="sm:hidden">Blog</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-28 sm:pt-36 pb-20 sm:pb-28 px-4 sm:px-6 min-h-[85vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-bottom bg-no-repeat" style={{ backgroundImage: "url('/hero-bg.jpg')" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/90 via-[#0A0A0A]/60 to-[#0A0A0A]" />

        <div className="relative z-10 max-w-4xl mx-auto text-center w-full">
          <div className="flex flex-wrap justify-center gap-3 mb-6 animate-fade-in-up opacity-0 stagger-1">
            <span className="inline-block px-4 py-2 rounded-full bg-[#1A1A1A] border border-[rgba(255,59,48,0.3)] text-[#FF3B30] text-sm font-medium">
              Importación de vehículos seleccionados
            </span>
            <span className="inline-block px-4 py-2 rounded-full bg-[#1A1A1A] border border-[rgba(0,122,255,0.3)] text-[#007AFF] text-sm font-medium">
              Búsqueda de vehículos nacionales
            </span>
            <span className="inline-block px-4 py-2 rounded-full bg-[#1A1A1A] border border-[rgba(52,199,89,0.3)] text-[#34C759] text-sm font-medium">
              Vehículos entrega inmediata
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold text-white mb-6 leading-tight animate-fade-in-up opacity-0 stagger-2" data-testid="hero-title">
            TU PRÓXIMO COCHE
            <span className="text-[#FF3B30] block mt-2">AL MEJOR PRECIO</span>
          </h1>
          <p className="text-base sm:text-lg text-[#8A8A8D] mb-12 max-w-2xl mx-auto animate-fade-in-up opacity-0 stagger-3">
            Importamos desde <strong className="text-white">Alemania</strong>, buscamos las mejores <strong className="text-white">ocasiones nacionales</strong> y tenemos <strong className="text-white">stock propio</strong> listo para entrega. Tú eliges, nosotros nos encargamos.
          </p>
        </div>
      </section>

      {/* 3 Service Cards */}
      <section className="relative z-10 -mt-20 px-4 sm:px-6 pb-20">
        <h2 className="sr-only">Nuestros servicios de compra de coches</h2>
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="service-cards">
          {/* Card 1: Importación */}
          <Link to="/importacion" className="group" data-testid="card-importacion">
            <div className="relative overflow-hidden rounded-2xl border border-[rgba(255,59,48,0.15)] hover:border-[#FF3B30]/50 transition-all duration-300 hover:-translate-y-1 h-full">
              <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/card-importacion.jpg')" }} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-[#0A0A0A]/40" />
              <div className="relative z-10 p-8 flex flex-col h-full min-h-[320px] justify-end">
                <div className="w-12 h-12 rounded-xl bg-[#FF3B30]/20 backdrop-blur-sm flex items-center justify-center mb-4 group-hover:bg-[#FF3B30]/30 transition-colors">
                  <Truck className="w-6 h-6 text-[#FF3B30]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Importación desde Alemania</h3>
                <p className="text-[#C4C4C6] text-sm leading-relaxed mb-5">
                  Pega el enlace de <strong className="text-white">mobile.de</strong> y te damos el precio final en tu puerta: transporte, impuestos, matriculación... todo incluido.
                </p>
                <div className="flex items-center gap-2 text-[#FF3B30] font-semibold text-sm group-hover:gap-3 transition-all">
                  Calcular importación <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </div>
          </Link>

          {/* Card 2: Nacionales */}
          <Link to="/ocasion" className="group" data-testid="card-nacionales">
            <div className="relative overflow-hidden rounded-2xl border border-[rgba(0,122,255,0.15)] hover:border-[#007AFF]/50 transition-all duration-300 hover:-translate-y-1 h-full">
              <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/card-nacionales.jpg')" }} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-[#0A0A0A]/40" />
              <div className="relative z-10 p-8 flex flex-col h-full min-h-[320px] justify-end">
                <div className="w-12 h-12 rounded-xl bg-[#007AFF]/20 backdrop-blur-sm flex items-center justify-center mb-4 group-hover:bg-[#007AFF]/30 transition-colors">
                  <Search className="w-6 h-6 text-[#007AFF]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Coches Nacionales</h3>
                <p className="text-[#C4C4C6] text-sm leading-relaxed mb-5">
                  Dinos qué buscas y nosotros lo encontramos. Vehículos de ocasión <strong className="text-white">revisados y garantizados</strong> del mercado español.
                </p>
                <div className="flex items-center gap-2 text-[#007AFF] font-semibold text-sm group-hover:gap-3 transition-all">
                  Buscar coche <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </div>
          </Link>

          {/* Card 3: Stock */}
          <Link to="/coches-disponibles" className="group" data-testid="card-stock">
            <div className="relative overflow-hidden rounded-2xl border border-[rgba(52,199,89,0.15)] hover:border-[#34C759]/50 transition-all duration-300 hover:-translate-y-1 h-full">
              <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/card-stock.jpg')" }} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-[#0A0A0A]/40" />
              <div className="relative z-10 p-8 flex flex-col h-full min-h-[320px] justify-end">
                <div className="w-12 h-12 rounded-xl bg-[#34C759]/20 backdrop-blur-sm flex items-center justify-center mb-4 group-hover:bg-[#34C759]/30 transition-colors">
                  <Car className="w-6 h-6 text-[#34C759]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Stock Disponible</h3>
                <p className="text-[#C4C4C6] text-sm leading-relaxed mb-5">
                  Coches ya importados, <strong className="text-white">revisados y listos</strong> para entrega inmediata. Sin esperas, sin sorpresas.
                </p>
                <div className="flex items-center gap-2 text-[#34C759] font-semibold text-sm group-hover:gap-3 transition-all">
                  Ver coches <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Stock Carousel */}
      {vehicles.length > 0 && (
        <section className="py-16 px-4 sm:px-6 border-t border-[rgba(138,138,141,0.1)]" data-testid="home-stock">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-white">COCHES <span className="text-[#34C759]">DISPONIBLES</span></h2>
                <p className="text-[#8A8A8D] text-sm mt-1">Importados, revisados y listos para entrega</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => scroll(stockRef, -1)} className="w-10 h-10 rounded-full bg-[#1A1A1A] border border-[rgba(138,138,141,0.15)] flex items-center justify-center text-[#8A8A8D] hover:text-white hover:border-[#34C759]/50 transition-colors">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button onClick={() => scroll(stockRef, 1)} className="w-10 h-10 rounded-full bg-[#1A1A1A] border border-[rgba(138,138,141,0.15)] flex items-center justify-center text-[#8A8A8D] hover:text-white hover:border-[#34C759]/50 transition-colors">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div ref={stockRef} className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide" style={{ scrollbarWidth: "none" }}>
              {vehicles.map(v => {
                const statusCls = v.status === 'vendido' ? 'bg-[#FF3B30]/20 text-[#FF3B30]' : v.status === 'reservado' ? 'bg-[#FF9500]/20 text-[#FF9500]' : v.status === 'en_proceso' ? 'bg-[#007AFF]/20 text-[#007AFF]' : 'bg-[#34C759]/20 text-[#34C759]';
                const statusText = v.status === 'vendido' ? 'Vendido' : v.status === 'reservado' ? 'Reservado' : v.status === 'en_proceso' ? 'En proceso' : 'Disponible';
                return (
                  <Link to="/coches-disponibles" key={v.id} className="glass-card overflow-hidden hover:border-[#34C759]/30 transition-all duration-300 group flex-shrink-0 w-[280px]">
                    <div className="relative aspect-[16/10] bg-[#111] overflow-hidden">
                      {v.images?.[0] ? (
                        <img src={`${API}/images/${v.images[0]}`} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#636366]"><Car className="w-10 h-10" /></div>
                      )}
                      <span className={`absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${statusCls}`}>{statusText}</span>
                    </div>
                    <div className="p-3">
                      <p className="text-white font-bold text-sm truncate">{v.title || `${v.make} ${v.model}`}</p>
                      <p className="text-[#8A8A8D] text-xs mt-1">{v.year} · {v.kilometers?.toLocaleString()} km</p>
                      <p className="text-[#34C759] font-bold text-lg mt-2">{v.price?.toLocaleString("es-ES")} €</p>
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="text-center mt-6">
              <Link to="/coches-disponibles">
                <Button className="bg-[#34C759] hover:bg-[#2DB84E] text-white h-11 px-6 rounded-xl">
                  <Car className="w-4 h-4 mr-2" /> Ver todos los coches
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Reviews Carousel */}
      {reviews.length > 0 && (
        <section className="py-16 px-4 sm:px-6 border-t border-[rgba(138,138,141,0.1)]" data-testid="home-reviews">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-white">LO QUE DICEN <span className="text-[#F5A623]">NUESTROS CLIENTES</span></h2>
                <p className="text-[#8A8A8D] text-sm mt-1">Experiencias reales de personas que confiaron en nosotros</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => scroll(reviewRef, -1)} className="w-10 h-10 rounded-full bg-[#1A1A1A] border border-[rgba(138,138,141,0.15)] flex items-center justify-center text-[#8A8A8D] hover:text-white hover:border-[#F5A623]/50 transition-colors">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button onClick={() => scroll(reviewRef, 1)} className="w-10 h-10 rounded-full bg-[#1A1A1A] border border-[rgba(138,138,141,0.15)] flex items-center justify-center text-[#8A8A8D] hover:text-white hover:border-[#F5A623]/50 transition-colors">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div ref={reviewRef} className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide" style={{ scrollbarWidth: "none" }}>
              {reviews.map(r => (
                <div key={r.id} className="glass-card p-6 flex-shrink-0 w-[340px] border-[rgba(245,166,35,0.1)] hover:border-[#F5A623]/30 transition-colors">
                  <Quote className="w-8 h-8 text-[#F5A623]/30 mb-3" />
                  <p className="text-[#C4C4C6] text-sm leading-relaxed mb-4">{r.text}</p>
                  <div className="flex items-center gap-3 pt-3 border-t border-[rgba(138,138,141,0.1)]">
                    {r.photo ? (
                      <img src={r.photo} alt={r.name} className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#F5A623]/15 flex items-center justify-center text-[#F5A623] font-bold text-sm">{r.name?.charAt(0)?.toUpperCase()}</div>
                    )}
                    <div>
                      <p className="text-white font-semibold text-sm">{r.name}</p>
                      <div className="flex gap-0.5 mt-0.5">
                        {[1,2,3,4,5].map(s => (
                          <Star key={s} className={`w-3 h-3 ${s <= r.stars ? 'text-[#F5A623] fill-[#F5A623]' : 'text-[#636366]'}`} />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why Us */}
      <section className="py-20 px-4 sm:px-6 border-t border-[rgba(138,138,141,0.1)]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white text-center mb-4">
            ¿POR QUÉ <span className="text-[#FF3B30]">TE LO BAJO</span>?
          </h2>
          <p className="text-[#8A8A8D] text-center mb-14 max-w-xl mx-auto">Transparencia, precio cerrado y trato personalizado de principio a fin.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: TrendingDown, color: "#FF3B30", title: "Mejor precio", desc: "Ahorra hasta un 30% frente al mercado español." },
              { icon: ShieldCheck, color: "#007AFF", title: "Sin sorpresas", desc: "Precio cerrado desde el primer momento. Lo que ves es lo que pagas." },
              { icon: Star, color: "#FF9500", title: "Seleccionados", desc: "Solo trabajamos con vehículos que pasan nuestro filtro de calidad." },
              { icon: Clock, color: "#34C759", title: "Rápido", desc: "Entrega en 2-4 semanas para importaciones. Stock listo al momento." },
            ].map((item, i) => (
              <div key={i} className="text-center p-6">
                <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: `${item.color}15` }}>
                  <item.icon className="w-6 h-6" style={{ color: item.color }} />
                </div>
                <h3 className="text-white font-bold mb-2">{item.title}</h3>
                <p className="text-[#8A8A8D] text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto text-center glass-card p-10 sm:p-14 border-[rgba(255,59,48,0.2)]">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-4">
            ¿LISTO PARA <span className="text-[#FF3B30]">EMPEZAR</span>?
          </h2>
          <p className="text-[#8A8A8D] mb-8">Elige el servicio que mejor se adapta a ti y te acompañamos en todo el proceso.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/importacion">
              <Button className="btn-red h-12 px-8 text-base rounded-xl" data-testid="cta-importacion">
                <Truck className="w-5 h-5 mr-2" /> Importar desde Alemania
              </Button>
            </Link>
            <Link to="/ocasion">
              <Button className="bg-[#007AFF] hover:bg-[#006AE0] text-white h-12 px-8 text-base rounded-xl" data-testid="cta-nacionales">
                <Search className="w-5 h-5 mr-2" /> Buscar nacional
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
